import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Button, Modal, Form, Input, Select, Space, InputNumber, message, Popconfirm } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { channelApi, agentServiceApi, agentApi, billingWeightRuleApi, carrierApi, type ChannelDTO, type AgentServiceChannelDTO, type AgentDTO, type BillingWeightRuleDTO, type CarrierDTO } from '../api/kfc';
import { clientTablePagination, DEFAULT_PAGE_SIZE } from '../utils/tablePagination';

const channelKey = (agentId?: string, channelCode?: string) => `${agentId ?? ''}::${channelCode ?? ''}`;

const ChannelManage: React.FC = () => {
  const [list, setList] = useState<ChannelDTO[]>([]);
  const [agentServices, setAgentServices] = useState<AgentServiceChannelDTO[]>([]);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [billingRules, setBillingRules] = useState<BillingWeightRuleDTO[]>([]);
  const [carriers, setCarriers] = useState<CarrierDTO[]>([]);
  const [filterAgentId, setFilterAgentId] = useState<string | undefined>();
  /** 新增弹窗内「选择代理渠道」的代理筛选（与列表筛选独立，打开时继承列表筛选） */
  const [modalFilterAgentId, setModalFilterAgentId] = useState<string | undefined>();
  /** 全量渠道，仅用于新增下拉排除已加入项（不受列表筛选影响） */
  const [allChannelsForExclude, setAllChannelsForExclude] = useState<ChannelDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  /** 从查询价格等页面带 channelId 跳转过来时定位的渠道 */
  const [highlightId, setHighlightId] = useState<string>();
  const [page, setPage] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });
  const locatePending = useRef(false);

  const matchAgentIds = (selected?: string) => {
    if (!selected) return null;
    const a = agents.find((x) => x.id === selected || x.basicInfo?.identifier === selected);
    return new Set(
      [selected, a?.id, a?.basicInfo?.identifier].filter((x): x is string => !!x)
    );
  };

  const agentLabel = (agentId?: string) => {
    if (!agentId) return '-';
    const a = agents.find(
      (x) => x.id === agentId || x.basicInfo?.identifier === agentId
    );
    if (!a) return agentId;
    const name = a.basicInfo?.name;
    const ident = a.basicInfo?.identifier ?? a.id;
    return name ? `${name} (${ident})` : agentId;
  };

  const load = (agentId?: string) => {
    setLoading(true);
    channelApi
      .list(agentId || undefined)
      .then((res) => setList(res.data || []))
      .catch(() => {
        setList([]);
        message.warning('无法加载渠道列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const channelId = searchParams.get('channelId');
    if (channelId) {
      setSearchParams({}, { replace: true });
      channelApi
        .get(channelId)
        .then((res) => {
          const c = res.data;
          setFilterAgentId(c.agentId || undefined);
          load(c.agentId || undefined);
          setHighlightId(c.id);
          locatePending.current = true;
          openEdit(c);
        })
        .catch(() => {
          message.warning('渠道不存在或已删除');
          load();
        });
    } else {
      load();
    }
    agentApi.list().then((res) => setAgents(res.data || [])).catch(() => setAgents([]));
    agentServiceApi.list().then((res) => setAgentServices(res.data || [])).catch(() => setAgentServices([]));
    billingWeightRuleApi.list().then((res) => setBillingRules(res.data || [])).catch(() => setBillingRules([]));
    carrierApi.list().then((res) => setCarriers(res.data || [])).catch(() => setCarriers([]));
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ agentServiceId: undefined });
    setModalFilterAgentId(filterAgentId);
    // 用全量渠道做排除，避免筛选态漏掉其它代理已加入项
    channelApi
      .list()
      .then((res) => setAllChannelsForExclude(res.data || []))
      .catch(() => setAllChannelsForExclude(list));
    setModalOpen(true);
  };

  const openEdit = (c: ChannelDTO) => {
    setEditingId(c.id);
    form.setFieldsValue({
      agentId: c.agentId,
      channelCode: c.channelCode,
      channelName: c.channelName,
      volumeCoefficient: c.volumeCoefficient,
      billingRuleId: c.billingRuleId,
      carrierId: c.carrierId,
    });
    setModalOpen(true);
  };

  const onAgentServiceSelect = (val: string) => {
    if (!val) return;
    const parts = val.split('::');
    const agentId = parts[0];
    const serviceId = parts[1];
    const svc = agentServices.find(
      (s) => s.agentId === agentId && String(s.serviceId) === serviceId
    );
    if (svc) {
      form.setFieldsValue({
        agentId: svc.agentId,
        channelCode: String(svc.serviceId),
        channelName: svc.serviceName ?? '',
      });
    }
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload: Partial<ChannelDTO> = {
      agentId: values.agentId as string,
      channelCode: values.channelCode as string,
      channelName: values.channelName as string | undefined,
      volumeCoefficient: values.volumeCoefficient as number | undefined,
      billingRuleId: (values.billingRuleId as string) || '',
      carrierId: (values.carrierId as string) || '',
    };
    if (editingId) {
      channelApi
        .update(editingId, payload)
        .then(() => {
          message.success('更新成功');
          setModalOpen(false);
          load(filterAgentId);
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'));
    } else {
      channelApi
        .save(payload)
        .then(() => {
          message.success('创建成功');
          setModalOpen(false);
          load(filterAgentId);
        })
        .catch((e) => message.error(e.response?.data?.message || '创建失败'));
    }
  };

  const onDelete = (id: string) => {
    channelApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load(filterAgentId);
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  useEffect(() => {
    if (!locatePending.current || !highlightId) return;
    const idx = list.findIndex((c) => c.id === highlightId);
    if (idx < 0) return;
    locatePending.current = false;
    setPage((p) => ({ ...p, current: Math.floor(idx / p.pageSize) + 1 }));
  }, [list, highlightId]);

  const usedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const c of allChannelsForExclude) {
      set.add(channelKey(c.agentId, c.channelCode));
    }
    return set;
  }, [allChannelsForExclude]);

  const agentFilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of agents) {
      const value = a.basicInfo?.identifier || a.id;
      if (!value) continue;
      const name = a.basicInfo?.name;
      map.set(value, name ? `${name} (${value})` : value);
    }
    // 并入渠道里出现过、但代理主数据缺失的 agentId
    for (const c of [...list, ...allChannelsForExclude]) {
      if (c.agentId && !map.has(c.agentId)) {
        map.set(c.agentId, c.agentId);
      }
    }
    for (const s of agentServices) {
      if (s.agentId && !map.has(s.agentId)) {
        map.set(s.agentId, s.agentId);
      }
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [agents, list, allChannelsForExclude, agentServices]);

  const agentServiceOptions = useMemo(() => {
    const ids = matchAgentIds(modalFilterAgentId);
    return agentServices
      .filter((s) => {
        if (ids && (!s.agentId || !ids.has(s.agentId))) return false;
        const key = channelKey(s.agentId, String(s.serviceId));
        return !usedKeys.has(key);
      })
      .map((s) => ({
        value: `${s.agentId}::${s.serviceId}`,
        label: `${agentLabel(s.agentId)} - ${s.serviceName ?? s.serviceId} (${s.stype ?? '-'})`,
      }));
  }, [agentServices, modalFilterAgentId, usedKeys, agents]);

  const dropdownPlaceholder =
    agentServiceOptions.length === 0
      ? modalFilterAgentId
        ? '该代理下已无更多可加入渠道'
        : '暂无可加入的代理渠道'
      : '请选择代理渠道';

  return (
    <>
      <Card
        title="渠道列表"
        extra={
          <Space wrap>
            <span>代理：</span>
            <Select
              allowClear
              placeholder="全部"
              style={{ minWidth: 220 }}
              value={filterAgentId}
              options={agentFilterOptions}
              showSearch
              optionFilterProp="label"
              onChange={(v) => {
                const next = v || undefined;
                setFilterAgentId(next);
                setHighlightId(undefined);
                setPage((p) => ({ ...p, current: 1 }));
                load(next);
              }}
            />
            <Button type="primary" onClick={openAdd}>
              新增渠道
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16 }}>
          新增渠道时，请从「代理渠道列表」中选择一个代理渠道，系统将带出渠道信息，可修改后保存。已加入本列表的代理渠道不会再出现在下拉中。
        </p>
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination({
            current: page.current,
            pageSize: page.pageSize,
            onChange: (current, pageSize) => setPage({ current, pageSize }),
          })}
          onRow={(c) => (c.id === highlightId ? { style: { background: '#e6f4ff' } } : {})}
          columns={[
            { title: '渠道编码', dataIndex: 'channelCode', width: 120 },
            { title: '渠道名称', dataIndex: 'channelName', width: 160, ellipsis: true },
            { title: '体积系数', dataIndex: 'volumeCoefficient', width: 100 },
            {
              title: '计费规则',
              dataIndex: 'billingRuleId',
              width: 140,
              ellipsis: true,
              render: (id: string) => {
                if (!id) return '-';
                const r = billingRules.find((x) => x.id === id);
                return r?.name || id;
              },
            },
            {
              title: '承运商',
              dataIndex: 'carrierName',
              width: 120,
              ellipsis: true,
              render: (v?: string) => v || '-',
            },
            {
              title: '代理',
              dataIndex: 'agentId',
              width: 180,
              ellipsis: true,
              render: (v: string) => agentLabel(v),
            },
            {
              title: '操作',
              width: 140,
              render: (_, c: ChannelDTO) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(c)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定删除该渠道？" onConfirm={() => onDelete(c.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑渠道' : '新增渠道（从代理渠道选择）'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {!editingId && (
            <>
              <Form.Item label="按代理筛选">
                <Select
                  allowClear
                  placeholder="全部代理"
                  style={{ width: '100%' }}
                  value={modalFilterAgentId}
                  options={agentFilterOptions}
                  showSearch
                  optionFilterProp="label"
                  onChange={(v) => {
                    setModalFilterAgentId(v || undefined);
                    form.setFieldsValue({
                      agentServiceId: undefined,
                      agentId: undefined,
                      channelCode: undefined,
                      channelName: undefined,
                    });
                  }}
                />
              </Form.Item>
              <Form.Item
                name="agentServiceId"
                label="选择代理渠道"
                rules={[{ required: true, message: '请从代理渠道列表中选择一个' }]}
              >
                <Select
                  placeholder={dropdownPlaceholder}
                  options={agentServiceOptions}
                  onChange={onAgentServiceSelect}
                  showSearch
                  disabled={agentServiceOptions.length === 0}
                  filterOption={(input, opt) =>
                    (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </>
          )}
          {!editingId && (
            <Form.Item name="agentId" hidden rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {editingId && (
            <Form.Item name="agentId" label="代理ID" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="channelCode" label="渠道编号" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="channelName" label="渠道名称">
            <Input />
          </Form.Item>
          <Form.Item name="volumeCoefficient" label="体积系数">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="如 5000" />
          </Form.Item>
          <Form.Item name="billingRuleId" label="计费重计算规则">
            <Select
              allowClear
              placeholder="可选，关联计费重规则"
              options={billingRules.map((r) => ({
                value: r.id,
                label: `${r.name}${r.compileStatus === 'OK' ? '' : ` (${r.compileStatus || '-'})`}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="carrierId" label="承运商" extra="用于运单转单号跳转承运商官网查询轨迹">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="可选，关联承运商"
              options={carriers.map((c) => ({
                value: c.id,
                label: `${c.carrierName || c.carrierCode}${c.isActive === false ? ' (已停用)' : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ChannelManage;
